import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ToastContext, Toast, ToastType } from './ToastContext'

function newToastId(): string {
  const c = globalThis?.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
}

interface ToastItemProps {
  toast: Toast
  onClose: (id: string) => void
  closeLabel: string
}

function ToastItem({ toast, onClose, closeLabel }: ToastItemProps) {
  const progressBarRef = useRef<HTMLDivElement>(null)
  const timeoutId = useRef<number | null>(null)
  const remainingTime = useRef(toast.ttlMs ?? 3500)
  const startTime = useRef(Date.now())
  const percentRemaining = useRef(1.0)
  const isPaused = useRef(false)

  const toneClass =
    toast.type === 'success'
      ? 'success'
      : toast.type === 'warning'
        ? 'warning'
        : 'error'

  const startTimer = useCallback(() => {
    if (remainingTime.current <= 0) return

    startTime.current = Date.now()

    const el = progressBarRef.current
    if (el) {
      el.style.transition = 'none'
      el.style.transform = `scaleX(${percentRemaining.current})`
      
      // Force layout reflow
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetWidth

      el.style.transition = `transform ${remainingTime.current}ms linear`
      el.style.transform = 'scaleX(0)'
    }

    timeoutId.current = window.setTimeout(() => {
      onClose(toast.id)
    }, remainingTime.current)
  }, [toast.id, onClose])

  const pauseTimer = useCallback(() => {
    if (isPaused.current || remainingTime.current <= 0) return
    isPaused.current = true

    if (timeoutId.current !== null) {
      clearTimeout(timeoutId.current)
      timeoutId.current = null
    }

    const elapsed = Date.now() - startTime.current
    remainingTime.current = Math.max(remainingTime.current - elapsed, 0)
    percentRemaining.current = remainingTime.current / (toast.ttlMs ?? 3500)

    const el = progressBarRef.current
    if (el) {
      el.style.transition = 'none'
      el.style.transform = `scaleX(${percentRemaining.current})`
    }
  }, [toast.ttlMs])

  const resumeTimer = useCallback(() => {
    if (!isPaused.current || remainingTime.current <= 0) return
    isPaused.current = false
    startTimer()
  }, [startTimer])

  useEffect(() => {
    if (toast.ttlMs && toast.ttlMs > 0) {
      startTimer()
    }

    return () => {
      if (timeoutId.current !== null) {
        clearTimeout(timeoutId.current)
      }
    }
  }, [toast.ttlMs, startTimer])

  return (
    <div
      className="admin-toast-item animate-toast-in"
      onMouseEnter={toast.ttlMs && toast.ttlMs > 0 ? pauseTimer : undefined}
      onMouseLeave={toast.ttlMs && toast.ttlMs > 0 ? resumeTimer : undefined}
    >
      <div className="admin-toast-body">
        <div className={`admin-toast-dot ${toneClass}`} />
        <div className="admin-toast-content">
          <div className="admin-toast-title">{toast.title}</div>
          {toast.message ? <div className="admin-toast-message">{toast.message}</div> : null}
        </div>
        <button
          type="button"
          onClick={() => onClose(toast.id)}
          className="admin-toast-close"
          aria-label={closeLabel}
        >
          ×
        </button>
      </div>
      
      {toast.ttlMs && toast.ttlMs > 0 ? (
        <div 
          ref={progressBarRef}
          className="admin-toast-progress" 
          style={{ transform: 'scaleX(1)' }}
        />
      ) : null}
    </div>
  )
}

interface ToastProviderProps {
  children: React.ReactNode
}

export default function ToastProvider({ children }: ToastProviderProps) {
  const { t } = useTranslation()
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const pushToast = useCallback((toast: {
    type?: ToastType
    title?: string
    message?: string
    ttlMs?: number
  }) => {
    const id = newToastId()
    const next: Toast = {
      id,
      type: toast.type ?? 'error',
      title: toast.title ?? t('common.errors.generic'),
      message: toast.message ?? '',
      ttlMs: toast.ttlMs ?? 3500,
    }

    setToasts((current) => [next, ...current].slice(0, 5))
    return id
  }, [t])

  const value = useMemo(() => ({ pushToast, removeToast }), [pushToast, removeToast])
  const closeLabel = useMemo(() => t('common.actions.close'), [t])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="admin-toast-container">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={removeToast} closeLabel={closeLabel} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
